class InfiniteScroll {
  constructor() {
    this.productGridContainer = document.querySelector('#ProductGridContainer');
    this.productGrid = document.querySelector('#product-grid');
    this.loading = false;
    this.allLoaded = false;
    this.currentPage = 1;
    this.prefetchedPage = null;
    
    if (!this.productGrid || !this.productGridContainer) {
      console.log('Product grid not found');
      return;
    }
    
    this.init();
  }

  init() {
    // Create loader and end message
    this.createLoader();
    this.createEndMessage();
    
    // Get initial next URL from pagination
    this.getNextUrl();
    
    // Hide pagination after getting URL
    this.hidePagination();
    
    // Prefetch next page immediately for instant loading
    if (this.nextUrl) {
      this.prefetchNextPage();
    }
    
    // Setup scroll listener
    this.setupScrollListener();
    
    console.log('Infinite scroll initialized. Next URL:', this.nextUrl);
  }

  hidePagination() {
    const pagination = document.querySelector('.pagination');
    if (pagination) {
      pagination.style.display = 'none';
    }
  }

  getNextUrl() {
    const nextLink = document.querySelector(
      '.pagination__item--next a, ' +
      '.pagination .pagination__item:last-child a, ' +
      'a[aria-label*="next" i], ' +
      'a[aria-label*="Next" i]'
    );
    
    if (nextLink && nextLink.href) {
      this.nextUrl = nextLink.href;
      console.log('Found next URL:', this.nextUrl);
    } else {
      console.log('No next page found - all products loaded');
      this.allLoaded = true;
      this.showEndMessage();
    }
  }

  // Prefetch next page in background for instant loading
  async prefetchNextPage() {
    if (!this.nextUrl || this.prefetchedPage) return;
    
    try {
      const response = await fetch(this.nextUrl);
      const html = await response.text();
      this.prefetchedPage = html;
      console.log('Next page prefetched and ready');
    } catch (error) {
      console.error('Prefetch error:', error);
    }
  }

  createLoader() {
    this.loader = document.createElement('div');
    this.loader.className = 'infinite-scroll-loader';
    this.loader.innerHTML = `
      <div class="loading-overlay__spinner">
        <svg aria-hidden="true" focusable="false" class="spinner" viewBox="0 0 66 66" xmlns="http://www.w3.org/2000/svg">
          <circle class="path" fill="none" stroke-width="6" cx="33" cy="33" r="30"></circle>
        </svg>
      </div>
    `;
    this.loader.style.display = 'none';
    this.productGridContainer.appendChild(this.loader);
  }

  createEndMessage() {
    this.endMessage = document.createElement('div');
    this.endMessage.className = 'infinite-scroll-end';
    this.endMessage.innerHTML = '<div class="center devEndScroll"></div>';
    this.endMessage.style.display = 'none';
    this.productGridContainer.appendChild(this.endMessage);
  }

  setupScrollListener() {
    let ticking = false;
    
    const scrollHandler = () => {
      if (!ticking) {
        window.requestAnimationFrame(() => {
          this.handleScroll();
          ticking = false;
        });
        ticking = true;
      }
    };
    
    window.addEventListener('scroll', scrollHandler);
    
    // Check on page load in case content is short
    setTimeout(() => this.handleScroll(), 300);
  }

  handleScroll() {
    if (this.loading || this.allLoaded) return;

    const scrollPosition = window.innerHeight + window.scrollY;
    // Trigger earlier (1200px before end) for smoother experience
    const threshold = document.documentElement.scrollHeight - 1200;

    if (scrollPosition >= threshold) {
      console.log('Scroll threshold reached, loading more...');
      this.loadMore();
    }
  }

  async loadMore() {
    if (!this.nextUrl || this.loading || this.allLoaded) {
      console.log('Cannot load more:', { nextUrl: this.nextUrl, loading: this.loading, allLoaded: this.allLoaded });
      return;
    }

    this.loading = true;
    this.loader.style.display = 'flex';
    
    console.log('Loading products...');

    try {
      let html;
      
      // Use prefetched page if available (instant!)
      if (this.prefetchedPage) {
        html = this.prefetchedPage;
        this.prefetchedPage = null;
        console.log('Using prefetched page - instant load!');
      } else {
        // Fetch if not prefetched
        const response = await fetch(this.nextUrl);
        if (!response.ok) {
          throw new Error(`HTTP error! status: ${response.status}`);
        }
        html = await response.text();
      }
      
      const parser = new DOMParser();
      const doc = parser.parseFromString(html, 'text/html');

      // Get new products
      const newProductGrid = doc.querySelector('#product-grid');
      
      if (!newProductGrid) {
        console.error('Product grid not found in response');
        this.allLoaded = true;
        this.showEndMessage();
        return;
      }
      
      const newProducts = newProductGrid.querySelectorAll(':scope > .grid__item');
      
      console.log('Found', newProducts.length, 'new products');
      
      if (newProducts.length === 0) {
        this.allLoaded = true;
        this.showEndMessage();
        return;
      }

      // Use DocumentFragment for faster DOM insertion
      const fragment = document.createDocumentFragment();
      const currentItems = this.productGrid.querySelectorAll('.grid__item').length;
      
      newProducts.forEach((product, index) => {
        const clonedProduct = product.cloneNode(true);
        
        // Update animation order
        if (clonedProduct.classList.contains('scroll-trigger')) {
          clonedProduct.style.setProperty('--animation-order', currentItems + index);
        }
        
        fragment.appendChild(clonedProduct);
      });
      
      // Single DOM update (much faster!)
      this.productGrid.appendChild(fragment);

      // Look for next page
      const nextLink = doc.querySelector(
        '.pagination__item--next a, ' +
        '.pagination .pagination__item:last-child a, ' +
        'a[aria-label*="next" i], ' +
        'a[aria-label*="Next" i]'
      );
      
      if (nextLink && nextLink.href) {
        this.nextUrl = nextLink.href;
        this.currentPage++;
        console.log('Next page URL:', this.nextUrl);
        
        // Immediately prefetch the next page in background
        this.prefetchNextPage();
      } else {
        console.log('No more pages');
        this.allLoaded = true;
        this.showEndMessage();
      }

      // Reinitialize Shopify features
      if (window.Shopify && window.Shopify.PaymentButton) {
        window.Shopify.PaymentButton.init();
      }

    } catch (error) {
      console.error('Error loading more products:', error);
      this.allLoaded = true;
      this.showEndMessage();
    } finally {
      this.loading = false;
      this.loader.style.display = 'none';
    }
  }

  showEndMessage() {
    this.loader.style.display = 'none';
    this.endMessage.style.display = 'block';
  }
}

// Initialize when DOM is ready
if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', () => {
    new InfiniteScroll();
  });
} else {
  new InfiniteScroll();
}

// Reinitialize after filtering
document.addEventListener('shopify:section:load', () => {
  setTimeout(() => {
    new InfiniteScroll();
  }, 100);
});
